import { useQuery } from '@apollo/client';
import {
  EEntityOwnership,
  EFilterType,
  TEntityOwnership,
  TFilter,
  TFilterEvent,
} from 'Components/Filter/filter.types';
import { SITES_TABLE_ROWS_PER_PAGE } from 'utils/pagination.utils';
import { IOrderBy } from 'DesignSystem/DataDisplay/Table/table.types';
import {
  ESiteTableColumnKeys,
  TSiteTableColumnKeys,
} from 'FeatureModules/Site/Components/SiteTable/SiteTable';
import {
  ESiteType,
  ESiteViewOption,
  IBaseSite,
  TSiteType,
} from 'FeatureModules/Site/types/site.types';
import { GET_SITES } from 'graphql/secure/queries/site.queries';
import { ISiteSummary } from 'graphql/secure/queries/types/site-query.types';
import { useLogEvent } from 'hooks/useLogEvent';
import { useEffect, useMemo, useState } from 'react';
import { IGraphQlConnection } from 'types/core.types';
import {
  ArrayParam,
  createEnumArrayParam,
  createEnumParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IUseSitesProps {
  // Can be used to skip the useQueryParam search and use the internal search term instead
  skipUseQueryParams?: boolean;
  skipQuery?: boolean;
  siteClusterId?: string;
  hasNoCluster?: boolean;
  excludeEmptyClusters?: boolean;
  clusterSitesType?: ESiteType | null;
  defaultCountryCodes?: string[];
  defaultRawMaterialIds?: string[];
  defaultOwnedById?: string;
  defaultViewOption?: ESiteViewOption | null;
  defaultSiteTypes?: ESiteType[];
}

interface IQueryState {
  searchTerm?: string | null;
  owner?: TEntityOwnership | null;
  ownedById?: string | null;
  siteTypes?: TSiteType[] | null;
  viewOption?: ESiteViewOption | null;
  orderByMethod?: 'ASC' | 'DESC' | undefined;
  orderByField?: TSiteTableColumnKeys | undefined;
  rawMaterialIds?: (string | null)[] | null;
  countryCodes?: (string | null)[] | null;
}

const useSites = ({
  skipUseQueryParams,
  skipQuery,
  siteClusterId,
  hasNoCluster,
  excludeEmptyClusters,
  clusterSitesType,
  defaultOwnedById,
  defaultViewOption = ESiteViewOption.CLUSTER,
  defaultSiteTypes = undefined,
  defaultCountryCodes,
  defaultRawMaterialIds,
}: IUseSitesProps = {}) => {
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, ''),
      owner: withDefault(
        createEnumParam<TEntityOwnership>(Object.values(EEntityOwnership)),
        defaultOwnedById ? EEntityOwnership.PARTNER : undefined
      ),
      ownedById: withDefault(StringParam, defaultOwnedById || undefined),
      siteTypes: withDefault(
        createEnumArrayParam<TSiteType>(Object.values(ESiteType)),
        defaultSiteTypes
      ),
      rawMaterialIds: withDefault(ArrayParam, defaultRawMaterialIds),
      countryCodes: withDefault(ArrayParam, defaultCountryCodes),
      viewOption: withDefault(
        createEnumParam<ESiteViewOption>([ESiteViewOption.CLUSTER, ESiteViewOption.LIST]),
        defaultViewOption || ESiteViewOption.CLUSTER
      ),
      orderByMethod: withDefault(createEnumParam<'ASC' | 'DESC'>(['ASC', 'DESC']), undefined),
      orderByField: withDefault(
        createEnumParam<TSiteTableColumnKeys>(Object.values(ESiteTableColumnKeys)),
        undefined
      ),
    },
    { removeDefaultsFromUrl: true }
  );

  const [internalQueryState, setInternalQueryState] = useState<IQueryState>({
    searchTerm: undefined,
    owner: defaultOwnedById ? EEntityOwnership.PARTNER : undefined,
    ownedById: defaultOwnedById,
    siteTypes: defaultSiteTypes,
    countryCodes: defaultCountryCodes,
    rawMaterialIds: defaultRawMaterialIds,
    viewOption: defaultViewOption,
    orderByMethod: undefined,
    orderByField: undefined,
  });

  const {
    searchTerm,
    owner: ownership,
    ownedById,
    siteTypes,
    viewOption,
    orderByMethod,
    orderByField,
    countryCodes: countryCodesParam,
    rawMaterialIds: rawMaterialIdsParam,
  } = skipUseQueryParams ? internalQueryState : query;

  // Filter out null values from ArrayParam results
  const countryCodes = countryCodesParam?.filter((c): c is string => c !== null);
  const rawMaterialIds = rawMaterialIdsParam?.filter((r): r is string => r !== null);

  const { logEvent } = useLogEvent();

  const orderBy =
    orderByMethod && orderByField
      ? {
          orderByField: orderByField,
          orderByMethod: orderByMethod,
        }
      : undefined;

  const { data, loading, error, fetchMore, refetch } = useQuery<{
    sites: IGraphQlConnection<IBaseSite, ISiteSummary>;
  }>(GET_SITES, {
    variables: {
      ...(searchTerm ||
      ownership ||
      siteClusterId ||
      defaultOwnedById ||
      siteTypes ||
      hasNoCluster ||
      countryCodes ||
      rawMaterialIds
        ? {
            filters: {
              ...(searchTerm ? { searchTerm } : {}),
              ...((defaultOwnedById || ownership === EEntityOwnership.PARTNER) && {
                ownedByIds: defaultOwnedById ? [defaultOwnedById] : [ownedById],
              }),
              ...(ownership && ownership !== EEntityOwnership.PARTNER
                ? { ownership: ownership }
                : { ownership: null }),
              ...(siteTypes ? { siteTypes } : {}),
              ...(siteClusterId ? { siteClusterId } : {}),
              ...(hasNoCluster ? { hasNoCluster } : {}),
              ...(excludeEmptyClusters ? { excludeEmptyClusters } : {}),
              ...(clusterSitesType ? { clusterSitesType } : {}),
              ...(countryCodes ? { countryCodes } : {}),
              ...(rawMaterialIds ? { rawMaterialIds } : {}),
            },
          }
        : {}),
      ...(searchTerm ||
      ownership ||
      siteClusterId ||
      defaultOwnedById ||
      siteTypes ||
      countryCodes ||
      rawMaterialIds
        ? {
            summaryFilters: {
              ...(searchTerm ? { searchTerm } : {}),
              ...((defaultOwnedById || ownership === EEntityOwnership.PARTNER) && {
                ownedByIds: defaultOwnedById ? [defaultOwnedById] : [ownedById],
              }),
              ...(siteTypes ? { siteTypes } : {}),
              ...(siteClusterId ? { siteClusterId } : {}),
              ...(countryCodes ? { countryCodes } : {}),
              ...(rawMaterialIds ? { rawMaterialIds } : {}),
            },
          }
        : {}),
      orderBy,
      siteViewOption: viewOption,
      first: SITES_TABLE_ROWS_PER_PAGE,
    },
    skip: skipQuery,
  });

  const sites: IBaseSite[] = useMemo(
    () => (data?.sites ? removeGraphConnections(data.sites) : []),
    [data]
  );

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.OWNERSHIP,
        value: ownership
          ? {
              option: ownership,
              ownedById: ownedById,
            }
          : undefined,
      },
      {
        type: EFilterType.SITE_TYPE,
        value: siteTypes,
        multiple: true,
      },
      {
        type: EFilterType.COUNTRIES,
        value: countryCodes,
        multiple: true,
      },
      {
        type: EFilterType.RAW_MATERIALS,
        value: rawMaterialIds,
        label: 'Raw materials',
        placeholder: 'All raw materials',
        multiple: true,
      },
    ],
    [ownership, ownedById, siteTypes, countryCodes, rawMaterialIds]
  );

  const hasNextPage: boolean = !!data?.sites?.pageInfo?.hasNextPage;
  const endCursor: string | undefined = data?.sites?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (!sites?.length && hasNextPage) {
      handlePageEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, hasNextPage]);

  const handleSearchTermChange = (searchTerm: string | undefined) => {
    logEvent('SITE_SEARCHED', { searchTerm });
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    setter((prev: IQueryState) => ({ ...prev, searchTerm }));
  };

  const handleSiteViewOptionChange = (viewOption: ESiteViewOption) => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    setter((prev: IQueryState) => ({ ...prev, viewOption }));
  };

  const handleFilterChange = (event: TFilterEvent) => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    if (event.type === EFilterType.OWNERSHIP) {
      setter((prev: IQueryState) => ({
        ...prev,
        owner: event.value?.option || undefined,
        ownedById: event.value?.ownedById || undefined,
      }));
    } else if (event.type === EFilterType.SITE_TYPE) {
      setter((prev: IQueryState) => ({
        ...prev,
        siteTypes: (event.value as TSiteType[]) || undefined,
      }));
    } else if (event.type === EFilterType.COUNTRIES) {
      setter((prev: IQueryState) => ({
        ...prev,
        countryCodes: (event.value as string[]) || undefined,
      }));
    } else if (event.type === EFilterType.RAW_MATERIALS) {
      setter((prev: IQueryState) => ({
        ...prev,
        rawMaterialIds: (event.value as string[]) || undefined,
      }));
    }
  };

  const handleFilterReset = () => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    setter((prev: IQueryState) => ({
      ...prev,
      owner: undefined,
      ownedById: undefined,
      siteTypes: undefined,
      countryCodes: undefined,
      rawMaterialIds: undefined,
    }));
  };

  const handleOrderByChange = (newOrderBy?: IOrderBy<TSiteTableColumnKeys>) => {
    const setter = skipUseQueryParams ? setInternalQueryState : setQuery;
    setter((prev: IQueryState) => ({
      ...prev,
      orderByMethod: newOrderBy?.orderByMethod,
      orderByField: newOrderBy?.orderByField,
    }));
  };

  return {
    sites,
    loading,
    summary: data?.sites?.summary,
    totalCount: data?.sites?.count,
    error,
    searchTerm,
    hasNextPage,
    filters,
    viewOption,
    orderBy,
    refetch,
    handleOrderByChange,
    handleSiteViewOptionChange,
    handleSearchTermChange,
    handlePageEndReached,
    handleFilterChange,
    handleFilterReset,
  };
};

export default useSites;
